import { create } from "zustand";
import type { FeedbackFlowState, FeedbackStep, FeedbackData, ConsentType, UserInfo, CompanyInfo, TestimonialAnswer, TestimonialData } from "../types";

const initialUserInfo: UserInfo = {
	name: "",
	email: "",
	photoFile: null,
	photoPreview: null,
};

const initialCompanyInfo: CompanyInfo = {
	jobTitle: "",
	companyName: "",
	websiteUrl: "",
	logoFile: null,
	logoPreview: null,
};

const initialTestimonialAnswer: TestimonialAnswer = {
	type: "text",
	textContent: "",
	videoFile: null,
	videoPreview: null,
};

const initialTestimonialData: TestimonialData = {
	question1: { ...initialTestimonialAnswer },
	question2: { ...initialTestimonialAnswer },
};

const initialData: FeedbackData = {
	rating: 0,
	negativeFeedback: "",
	consent: null,
	userInfo: initialUserInfo,
	companyInfo: initialCompanyInfo,
	testimonialData: initialTestimonialData,
};

// Step flow logic for positive path (4-5 stars)
const positiveStepOrder: FeedbackStep[] = ["rating", "testimonial-intro", "testimonial-q1", "testimonial-q2", "consent", "about-user", "about-company", "thank-you"];

// Step flow logic for negative path (1-3 stars)
const negativeStepOrder: FeedbackStep[] = ["rating", "negative-feedback", "thank-you"];

export const useFeedbackFlowStore = create<FeedbackFlowState>((set, get) => ({
	currentStep: "rating",
	data: initialData,
	isDirty: false,
	isComplete: false,

	setRating: (rating: number) => {
		set((state) => ({
			data: { ...state.data, rating },
			isDirty: true,
		}));
	},

	setNegativeFeedback: (feedbackText: string) => {
		set((state) => ({
			data: {
				...state.data,
				negativeFeedback: feedbackText,
			},
			isDirty: true,
		}));
	},

	setConsent: (consent: ConsentType) => {
		set((state) => ({
			data: { ...state.data, consent },
			isDirty: true,
		}));
	},

	setUserInfo: (info: Partial<UserInfo>) => {
		set((state) => ({
			data: {
				...state.data,
				userInfo: { ...state.data.userInfo, ...info },
			},
			isDirty: true,
		}));
	},

	setCompanyInfo: (info: Partial<CompanyInfo>) => {
		set((state) => ({
			data: {
				...state.data,
				companyInfo: { ...state.data.companyInfo, ...info },
			},
			isDirty: true,
		}));
	},

	setTestimonialAnswer: (question: "question1" | "question2", answer: Partial<TestimonialAnswer>) => {
		set((state) => ({
			data: {
				...state.data,
				testimonialData: {
					...state.data.testimonialData,
					[question]: { ...state.data.testimonialData[question], ...answer },
				},
			},
			isDirty: true,
		}));
	},

	nextStep: () => {
		const { currentStep, data } = get();
		const isNegative = data.rating >= 1 && data.rating <= 3;
		const stepOrder = isNegative ? negativeStepOrder : positiveStepOrder;

		const currentIndex = stepOrder.indexOf(currentStep);
		if (currentIndex < stepOrder.length - 1) {
			set({ currentStep: stepOrder[currentIndex + 1] });
		}
	},

	prevStep: () => {
		const { currentStep, data } = get();
		const isNegative = data.rating >= 1 && data.rating <= 3;
		const stepOrder = isNegative ? negativeStepOrder : positiveStepOrder;

		const currentIndex = stepOrder.indexOf(currentStep);
		if (currentIndex > 0) {
			set({ currentStep: stepOrder[currentIndex - 1] });
		}
	},

	canGoBack: () => {
		const { currentStep, data } = get();
		const isNegative = data.rating >= 1 && data.rating <= 3;
		const stepOrder = isNegative ? negativeStepOrder : positiveStepOrder;

		const currentIndex = stepOrder.indexOf(currentStep);
		return currentIndex > 0 && currentStep !== "thank-you";
	},

	canProceed: () => {
		const { currentStep, data } = get();

		const hasValidTestimonialAnswer = (answer: TestimonialAnswer): boolean => {
			if (answer.type === "text") {
				return answer.textContent.trim().length > 0;
			}
			return answer.videoFile !== null;
		};

		switch (currentStep) {
			case "rating":
				return data.rating >= 1 && data.rating <= 5;
			case "negative-feedback":
				return data.negativeFeedback.trim().length > 0;
			case "motivation":
				return true; // Just viewing content
			case "testimonial-intro":
				return true; // Just viewing content
			case "testimonial-q1":
				return hasValidTestimonialAnswer(data.testimonialData.question1);
			case "testimonial-q2":
				return hasValidTestimonialAnswer(data.testimonialData.question2);
			case "consent":
				return data.consent !== null;
			case "about-user":
				return data.userInfo.name.trim().length > 0 && data.userInfo.email.trim().length > 0;
			case "about-company":
				return true; // All fields optional
			default:
				return false;
		}
	},

	reset: () => {
		set({
			currentStep: "rating",
			data: initialData,
			isDirty: false,
			isComplete: false,
		});
	},

	complete: () => {
		set({ isComplete: true, isDirty: false });
	},
}));
